import { useCallback, useEffect, useState } from "react";

import { Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  IconButton,
  Stack,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGrid } from "@mui/x-data-grid";
import { PageContainer } from "@/features/ui";

import { useAuth } from "@/features/authentication";
import { PermissionEnum } from "@/features/permissions";
import {
  AddPromotionCampaignFormDialog,
  EditPromotionCampaignFormDialog,
} from "@/features/promotion-campaigns";
import { apiClient, getErrorMessage } from "@/features/shared";
import { CustomDialogTitle, SearchTextField, useSnackbar } from "@/features/ui";
import { useSearchFilter, useServerPagination } from "@/hooks";

export const PromotionCampaigns = () => {
  const { hasPermission } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [promotionCampaignId, setPromotionCampaignId] = useState(null);
  const [deleteDialogCampaign, setDeleteDialogCampaign] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [promotionCampaigns, setPromotionCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);

  const { setRowCount, paginationParams, dataGridPaginationProps, resetParams } =
    useServerPagination();
  const { keyword, searchProps } = useSearchFilter({ resetParams });

  const loadPromotionCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data, pageInfo },
      } = await apiClient.get("/promotion-campaigns", {
        params: {
          ...paginationParams,
          ...(keyword && { name: keyword }),
        },
      });
      setPromotionCampaigns(data);
      setRowCount(pageInfo.totalItems);
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal memuat kampanye promosi"));
    } finally {
      setLoading(false);
    }
  }, [paginationParams, keyword, setRowCount, showSnackbar]);

  useEffect(() => {
    loadPromotionCampaigns();
  }, [loadPromotionCampaigns]);

  const handleDelete = async () => {
    if (!deleteDialogCampaign) return;

    try {
      setIsDeleting(true);
      await apiClient.delete(`promotion-campaigns/${deleteDialogCampaign.id}`);
      showSnackbar("Kampanye promosi berhasil dihapus", "success");
      setDeleteDialogCampaign(null);
      loadPromotionCampaigns();
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal menghapus kampanye promosi"));
    } finally {
      setIsDeleting(false);
    }
  };

  const toolbarContent = hasPermission(PermissionEnum.CREATE_PROMOTIONS) && (
        <Button
          variant="contained"
          onClick={() => setIsAddDialogOpen(true)}
        >
          Tambah
        </Button>
      );

  return (
    <PageContainer maxWidth="xl" breadcrumbs={[]} slotProps={{ header: { slotProps: { toolbar: { children: toolbarContent } } } }}>
      <Stack spacing={3}>
        <Grid container sx={{ justifyContent: "flex-end" }}>
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <SearchTextField
              fullWidth
              placeholder="Cari nama kampanye promosi"
              {...searchProps}
            />
          </Grid>
        </Grid>
        <DataGrid
          rows={promotionCampaigns}
          loading={loading}
          getRowHeight={() => "auto"}
          columns={[
            {
              field: "name",
              headerName: "Nama",
              flex: 3,
              sortable: false,
            },
            {
              field: "description",
              headerName: "Deskripsi",
              flex: 3,
              sortable: false,
            },
            ...(hasPermission(PermissionEnum.UPDATE_PROMOTIONS) ||
            hasPermission(PermissionEnum.DELETE_PROMOTIONS)
              ? [
                  {
                    field: "actions",
                    type: "actions",
                    headerName: "Aksi",
                    getActions: (params) => [
                      ...(hasPermission(PermissionEnum.UPDATE_PROMOTIONS)
                        ? [
                            <IconButton
                              key="edit"
                              onClick={() => setPromotionCampaignId(params.id)}
                              size="small"
                            >
                              <EditIcon />
                            </IconButton>,
                          ]
                        : []),
                      ...(hasPermission(PermissionEnum.DELETE_PROMOTIONS)
                        ? [
                            <IconButton
                              key="delete"
                              onClick={() => setDeleteDialogCampaign(params.row)}
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>,
                          ]
                        : []),
                    ],
                  },
                ]
              : []),
          ]}
          {...dataGridPaginationProps}
          disableRowSelectionOnClick
        />
        <AddPromotionCampaignFormDialog
          isOpen={isAddDialogOpen}
          onClose={() => setIsAddDialogOpen(false)}
          onSubmitted={loadPromotionCampaigns}
        />
        <EditPromotionCampaignFormDialog
          promotionCampaignId={promotionCampaignId}
          isOpen={!!promotionCampaignId}
          onClose={() => setPromotionCampaignId(null)}
          onSubmitted={loadPromotionCampaigns}
        />
        {!!deleteDialogCampaign && (
          <Dialog
            open
            onClose={() => !isDeleting && setDeleteDialogCampaign(null)}
            maxWidth="xs"
            fullWidth
          >
            <CustomDialogTitle
              title="Hapus Kampanye Promosi"
              onClose={() => !isDeleting && setDeleteDialogCampaign(null)}
            />
            <DialogContent>
              <DialogContentText>
                Apakah Anda yakin ingin menghapus kampanye promosi{" "}
                <strong>{deleteDialogCampaign.name}</strong>?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setDeleteDialogCampaign(null)}
                disabled={isDeleting}
              >
                Batal
              </Button>
              <Button
                onClick={handleDelete}
                variant="contained"
                color="error"
                disabled={isDeleting}
              >
                {isDeleting ? "Menghapus..." : "Hapus"}
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Stack>
    </PageContainer>
  );
};
